import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { QrCode, IndianRupee, Timer, X, Store, KeyRound, Gift } from "lucide-react";
import QRCode from "qrcode";
import { openReceipt } from "@/lib/receipt";
import PinGate from "@/components/PinGate";
import { useAuth } from "@/lib/auth";

const statusColor = (s) => ({
  approved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  paid: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  rejected: "bg-red-500/20 text-red-300 border-red-500/30",
  refunded: "bg-red-500/20 text-red-300 border-red-500/30",
}[s] || "");

// The customer never cares about the shop-payout workflow: from their
// perspective a scanned redemption is already "Confirmed". Rejections come
// back as refunds.
const customerLabel = (r) => {
  if (r.type === "shop_scan") {
    if (r.status === "rejected") return "refunded";
    return "confirmed";
  }
  return r.status;
};

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = Math.max(0, Math.floor((new Date(target).getTime() - now) / 1000));
  const mm = String(Math.floor(diff / 60)).padStart(2, "0");
  const ss = String(diff % 60).padStart(2, "0");
  return { seconds: diff, label: `${mm}:${ss}` };
}

function ActiveIntentCard({ intent, onCancel, onExpired }) {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const cd = useCountdown(intent?.expires_at);

  useEffect(() => {
    if (intent?.code) {
      // Encode ONLY the short redemption code — dense JWT QRs are hard to
      // decode from phone cameras. Backend accepts either token or code.
      QRCode.toDataURL(intent.code, { errorCorrectionLevel: "H", margin: 2, width: 260 })
        .then(setQrDataUrl).catch(() => setQrDataUrl(""));
    }
  }, [intent?.code]);

  useEffect(() => {
    if (cd && cd.seconds === 0) onExpired?.();
  }, [cd, onExpired]);

  if (!intent) return null;
  return (
    <div data-testid="active-intent-card" className="velo-card p-5 sm:p-6 border-2 border-pink-500/40">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-pink-400">Active redemption</div>
          <div className="text-white text-lg font-medium mt-1">Show this to the tie-up shop</div>
          <div className="text-xs text-slate-400 mt-0.5">The shop will scan the QR or enter the code below.</div>
        </div>
        <Button data-testid="btn-cancel-intent" size="sm" variant="outline" onClick={onCancel} className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5 h-9">
          <X className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Cancel
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-5 items-center">
        <div className="bg-white p-3 rounded-lg mx-auto sm:mx-0">
          {qrDataUrl ? <img src={qrDataUrl} alt="QR" className="w-56 h-56" data-testid="intent-qr-image" /> : <div className="w-56 h-56 animate-pulse bg-slate-200" />}
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Redemption code</div>
            <div data-testid="intent-code" className="text-3xl text-white font-mono tracking-widest mt-1">{intent.code}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="velo-surface p-3 rounded">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Points</div>
              <div className="text-white font-mono mt-1">{intent.points.toLocaleString("en-IN")}</div>
            </div>
            <div className="velo-surface p-3 rounded">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Value</div>
              <div className="text-emerald-300 font-mono mt-1">₹{Number(intent.amount).toLocaleString("en-IN")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-amber-300 text-sm">
            <Timer className="w-4 h-4" strokeWidth={2} />
            <span data-testid="intent-countdown" className="font-mono">Expires in {cd?.label || "00:00"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RedemptionPanel({ role, onChange }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [intent, setIntent] = useState(null);
  const [openQR, setOpenQR] = useState(false);
  const [openC, setOpenC] = useState(false);
  const [redeemPts, setRedeemPts] = useState("");
  const [redeemPin, setRedeemPin] = useState("");
  const [cashPts, setCashPts] = useState("");

  // Hubble gift-card SDK — embedded in a modal iframe.
  const [openHubble, setOpenHubble] = useState(false);
  const [hubbleUrl, setHubbleUrl] = useState("");
  const [hubbleLoading, setHubbleLoading] = useState(false);
  const availableBalance = Math.max(0, (user?.points_balance || 0) - (user?.held_points || 0));

  const launchHubble = async () => {
    if (availableBalance <= 0) return;
    setHubbleLoading(true);
    try {
      const { data } = await api.post("/hubble/sso-token");
      // Merge the token + clientId into whatever query string the base URL
      // already carries. Using URLSearchParams guarantees each param appears
      // exactly once even if Hubble's base URL is later configured with
      // ?clientId=… baked in.
      const url = new URL(data.sdk_base_url || "");
      url.searchParams.set("token", data.token);
      if (data.client_id) url.searchParams.set("clientId", data.client_id);
      setHubbleUrl(url.toString());
      setOpenHubble(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open Hubble. Please try again.");
    } finally {
      setHubbleLoading(false);
    }
  };

  const closeHubble = () => {
    setOpenHubble(false);
    // Drop the URL so the iframe unloads (session expires quickly anyway).
    setHubbleUrl("");
    // Balance may have changed if the user completed a redemption.
    onChange?.();
    load();
  };

  const load = useCallback(async () => {
    const [r1, r2] = await Promise.all([
      api.get("/redemptions/my"),
      api.get("/redemptions/intent/active"),
    ]);
    setRows(r1.data);
    setIntent(r2.data);
    return { redemptions: r1.data, intent: r2.data };
  }, []);
  useEffect(() => { load(); }, [load]);

  // Real-time polling — while an intent is active, watch for it flipping to scanned
  // (shop just scanned) or expired. On transition, refresh redemptions + parent balance.
  useEffect(() => {
    if (!intent) return undefined;
    const id = setInterval(async () => {
      try {
        const { data: fresh } = await api.get("/redemptions/intent/active");
        if (!fresh) {
          // Intent disappeared (scanned/expired/cancelled) — pull redemptions & balance now
          const { data: rd } = await api.get("/redemptions/my");
          setRows(rd);
          setIntent(null);
          onChange?.();
          const latest = rd.find((r) => r.type === "shop_scan" && r.status === "pending");
          if (latest) toast.success(`Scanned at ${latest.shop_name} — awaiting accountant`);
        } else {
          setIntent(fresh);
        }
      } catch (e) { console.debug("intent poll failed:", e?.message); }
    }, 4000);
    return () => clearInterval(id);
  }, [intent, onChange]);

  // Also poll the redemptions list periodically so status changes (pending -> paid/rejected)
  // reflect without a manual refresh.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/redemptions/my");
        setRows((prev) => {
          // If any row changed status, notify parent so balance refreshes.
          const prevMap = Object.fromEntries((prev || []).map((r) => [r.id, r.status]));
          const changed = data.some((r) => prevMap[r.id] && prevMap[r.id] !== r.status);
          if (changed) onChange?.();
          return data;
        });
      } catch (e) { console.debug("redemptions poll failed:", e?.message); }
    }, 15000);
    return () => clearInterval(id);
  }, [onChange]);

  const createIntent = async () => {
    const pts = parseInt(redeemPts);
    if (!pts || pts <= 0) return toast.error("Enter a valid points amount");
    if (!/^\d{4}$/.test(redeemPin)) return toast.error("Enter your 4-digit redemption PIN");
    try {
      const { data } = await api.post("/redemptions/intent", { points: pts, pin: redeemPin });
      setIntent(data);
      setOpenQR(false);
      setRedeemPts("");
      setRedeemPin("");
      toast.success("Redemption code generated — valid for 10 minutes");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate code");
    }
  };

  const cancelIntent = async () => {
    if (!intent) return;
    try {
      await api.delete(`/redemptions/intent/${intent.id}`);
      setIntent(null);
      toast.success("Redemption code cancelled");
      onChange?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  const onIntentExpired = useCallback(async () => {
    // Refresh state — backend auto-expires and releases hold on read
    await load();
    onChange?.();
  }, [load, onChange]);

  const submitCash = async () => {
    try {
      await api.post("/redemptions/cash", { points: parseInt(cashPts) });
      toast.success("Cash payout requested");
      setOpenC(false); setCashPts("");
      load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <PinGate>
      {({ status: pinStatus, openReset }) => (
        <div className="space-y-5">
          {intent ? (
            <ActiveIntentCard intent={intent} onCancel={cancelIntent} onExpired={onIntentExpired} />
          ) : null}

      <div className="velo-card p-5 sm:p-6 lg:p-8">
        <div className="flex items-start sm:items-center justify-between mb-5 sm:mb-6 flex-col sm:flex-row gap-3">
          <div>
            <h2 className="text-lg sm:text-xl text-white font-medium">Redemptions</h2>
            <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Redeem points at tie-up shops{role === "affiliate" ? " or request cash payouts" : ""}</p>
          </div>
          <div className="flex gap-2 flex-wrap w-full sm:w-auto">
            <Dialog open={openQR} onOpenChange={setOpenQR}>
              <DialogTrigger asChild>
                <Button data-testid="btn-redeem-shop" disabled={!!intent} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black h-11 flex-1 sm:flex-none disabled:opacity-40">
                  <QrCode className="w-4 h-4 mr-2" strokeWidth={1.5} /> Redeem at tie-up shop
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#22103F] border-white/10 text-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Redeem at a tie-up shop</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-slate-300">Points to redeem</Label>
                    <Input data-testid="input-intent-points" type="number" value={redeemPts} onChange={(e) => setRedeemPts(e.target.value)} placeholder="e.g. 500" className="velo-input mt-2 font-mono" />
                  </div>
                  <div>
                    <Label className="text-slate-300 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" />Redemption PIN</Label>
                    <Input data-testid="input-intent-pin" type="password" inputMode="numeric" maxLength={4} value={redeemPin} onChange={(e) => setRedeemPin(e.target.value.replace(/\D/g, ""))} placeholder="4-digit PIN" className="velo-input mt-2 font-mono text-center tracking-[0.5em] text-lg" />
                  </div>
                  <div className="velo-surface p-3 rounded text-xs text-slate-400 flex items-start gap-2">
                    <Store className="w-4 h-4 text-pink-400 mt-0.5 shrink-0" strokeWidth={2} />
                    <div>
                      We'll generate a QR + redemption code valid for <span className="text-white">10 minutes</span>. Show it to any Veloskin tie-up shop; they'll scan it and apply the discount. 1 point = ₹1.
                    </div>
                  </div>
                </div>
                <DialogFooter className="flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <button
                    data-testid="btn-forgot-pin"
                    type="button"
                    onClick={openReset}
                    className="text-xs text-slate-400 hover:text-pink-300 underline underline-offset-2"
                  >
                    Forgot PIN?
                  </button>
                  <Button data-testid="btn-submit-intent" onClick={createIntent} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">Generate redemption code</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {role === "affiliate" && (
              <Dialog open={openC} onOpenChange={setOpenC}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-redeem-cash" variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 h-11 flex-1 sm:flex-none">
                    <IndianRupee className="w-4 h-4 mr-2" strokeWidth={1.5} /> Request cash payout
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#22103F] border-white/10 text-white max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Request cash payout</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div>
                      <Label className="text-slate-300">Points to encash</Label>
                      <Input data-testid="input-cash-points" type="number" value={cashPts} onChange={(e) => setCashPts(e.target.value)} className="velo-input mt-2 font-mono" />
                    </div>
                    <p className="text-xs text-slate-400">Payout will be processed offline by admin to your saved bank account. 1 point ≈ ₹1.</p>
                  </div>
                  <DialogFooter>
                    <Button data-testid="btn-submit-cash" onClick={submitCash} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">Submit request</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {availableBalance > 0 && (
              <Button
                data-testid="btn-redeem-hubble"
                onClick={launchHubble}
                disabled={hubbleLoading}
                variant="outline"
                className="rounded-full border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 h-11 flex-1 sm:flex-none disabled:opacity-40"
              >
                <Gift className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {hubbleLoading ? "Opening…" : "Redeem as Voucher (500 brands)"}
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} data-testid={`redemption-row-${r.id}`} className="flex items-center justify-between p-4 velo-surface rounded-lg flex-wrap gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white capitalize">{r.type === "shop_scan" ? "Shop redemption" : `${r.type} redemption`}</span>
                  <Badge className={`text-xs border ${statusColor(customerLabel(r))} capitalize`}>{customerLabel(r)}</Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {r.redemption_code && <span className="font-mono text-pink-400">{r.redemption_code}</span>}
                  {r.shop_name && <span className="ml-2">· {r.shop_name}</span>}
                  {r.amount > 0 && <span className="ml-2">· ₹{Number(r.amount).toLocaleString("en-IN")}</span>}
                  <span className="ml-2">· {format(new Date(r.created_at), "dd MMM yyyy")}</span>
                </div>
                {r.receipt_url && (
                  <button
                    type="button"
                    data-testid={`btn-view-receipt-${r.id}`}
                    onClick={() => openReceipt(r.receipt_url)}
                    className="text-xs text-pink-400 hover:underline mt-1 inline-block"
                  >
                    View receipt →
                  </button>
                )}
              </div>
              <div className="font-mono text-sm text-red-400">-{r.points} pts</div>
            </div>
          ))}
          {rows.length === 0 && <div className="text-slate-500 text-center py-6 text-sm">No redemptions yet</div>}
        </div>
      </div>

      <Dialog open={openHubble} onOpenChange={(v) => (v ? setOpenHubble(true) : closeHubble())}>
        <DialogContent
          data-testid="hubble-sdk-modal"
          className="bg-[#170929] border-white/10 text-white p-0 max-w-[95vw] w-[900px] h-[85vh] overflow-hidden"
        >
          <DialogHeader className="px-5 pt-4 pb-2 border-b border-white/10">
            <DialogTitle className="flex items-center gap-2 text-base font-medium">
              <Gift className="w-4 h-4 text-fuchsia-300" strokeWidth={1.5} />
              Redeem as Voucher (500 brands)
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400 mt-1">
              Available: <span className="text-white font-mono">{availableBalance.toLocaleString("en-IN")}</span> pts · 1 point = ₹1
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 h-[calc(85vh-64px)] bg-white">
            {hubbleUrl ? (
              <iframe
                data-testid="hubble-sdk-iframe"
                title="Hubble Gift Cards"
                src={hubbleUrl}
                allow="clipboard-write; payment"
                className="w-full h-full border-0"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">Loading Hubble…</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
        </div>
      )}
    </PinGate>
  );
}
